import { AlertTriangle } from 'lucide-react'

import { StatusDot } from '@/components/ui/status-dot'

export interface MachineStatusIndicatorProps {
  status: string
  statusCategory: 'operational' | 'needs-attention' | 'offline'
  emptySlots: number
}

const STATUS_LABELS: Record<string, string> = {
  offline: 'Offline',
  unknown: 'Unknown',
  'error ( door opened unexpectedly)': 'Door opened',
  'error ( dispenser error detected)': 'Unclosed drawer',
}

function resolveOfflineLabel(status: string): string {
  const lower = status.toLowerCase()
  const mapped = STATUS_LABELS[lower]
  if (mapped !== undefined) return mapped
  if (lower.startsWith('error')) {
    const stripped = status
      .replace(/^error\s*\(?/i, '')
      .replace(/\)$/, '')
      .trim()
    return stripped.length > 0 ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : 'Error'
  }
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function MachineStatusIndicator({
  status,
  statusCategory,
  emptySlots,
}: MachineStatusIndicatorProps): React.ReactElement {
  const isOffline = statusCategory === 'offline'
  const hasEmptySlots = !isOffline && emptySlots > 0

  if (isOffline) {
    return (
      <div className="flex items-center gap-2.5">
        <AlertTriangle className="text-destructive size-5" />
        <span className="text-destructive text-sm font-semibold">
          {resolveOfflineLabel(status)}
        </span>
        <StatusDot variant="destructive" />
      </div>
    )
  }

  if (hasEmptySlots) {
    return (
      <div className="flex items-center gap-2.5">
        <AlertTriangle className="text-warning size-5" />
        <span className="text-warning text-sm font-semibold">{emptySlots} low stock</span>
        <StatusDot variant="warning" />
      </div>
    )
  }

  return <StatusDot variant="success" />
}
